import React, { useEffect, useState } from "react";
import type { I18njs } from "@procore/core-i18n-js";
import { Box, Panel, Switch, MultiSelect, Token } from "@procore/core-react";
import type { Filter } from "@laborchart-modules/common/dist/rethink/schemas/generated-reports/common";
import type { TagOption, JobTitle } from "@/react/prop-types";
import type { OptionItem } from "../../data-table/custom-filters/multiselect-pill-filter";
import MultiSelectPillFilter from "../../data-table/custom-filters/multiselect-pill-filter";
import {
   useGetJobTitles,
   useGetProjectCustomFieldFilters,
   useGetTagOptionsQuery,
   useGetProjectFilterOptionsQuery,
} from "../../common/queries/queries";
import { useGroupContext } from "@/react/providers/group-context-provider";
import type { BackwardsCompatibleRolesSelection } from "@/react/prop-types";
import { ProjectRolesFilter } from "../../data-table/custom-filters/project-roles-filter";
import type { SelectedProjectFilterOption } from "@/react/prop-types";
import { ProjectCustomFieldFilter } from "./project-custom-field-filter";
import "./index.css";

export type BoardsFilterTypes = {
   projectTags: OptionItem[];
   resourceStatuses: Array<{ id: number; name: string }>;
   jobTitles: JobTitle[];
   hideEmptyProjects: boolean;
   hideLabor: boolean;
   hideEquipment: boolean;
   hideAssignments: boolean;
   hideRequests: boolean;
   projectRoles: BackwardsCompatibleRolesSelection;
   selectedProjects: SelectedProjectFilterOption[];
   projectCustomFields: Filter[];
};

export type BoardsFilterOptionTypes =
   | "projectTags"
   | "resourceStatuses"
   | "jobTitles"
   | "hideEmptyProjects"
   | "hideLabor"
   | "hideEquipment"
   | "hideAssignments"
   | "hideRequests"
   | "projectRoles"
   | "selectedProjects";

export const statuses = [
   { id: 0, name: "Active" },
   { id: 1, name: "Inactive" },
   { id: 2, name: "Pending" },
];

export function getDefaultBoardsFilter(): BoardsFilterTypes {
   return {
      projectTags: [],
      resourceStatuses: [],
      jobTitles: [],
      hideEmptyProjects: true,
      hideLabor: false,
      hideEquipment: false,
      hideAssignments: false,
      hideRequests: false,
      projectRoles: [],
      selectedProjects: [],
      projectCustomFields: [],
   };
}

export function getClearedBoardsFilter(): BoardsFilterTypes {
   const defaultFilter = getDefaultBoardsFilter();
   const clearedFilter: Partial<BoardsFilterTypes> = {};

   // Programmatically set all values to their "cleared" state
   for (const key in defaultFilter) {
      const value = defaultFilter[key as keyof BoardsFilterTypes];
      if (Array.isArray(value)) {
         // Clear arrays to empty arrays
         (clearedFilter as any)[key] = [];
      } else if (typeof value === "boolean") {
         // Clear booleans to false
         (clearedFilter as any)[key] = false;
      } else {
         // For any other types, use the default value
         (clearedFilter as any)[key] = value;
      }
   }

   return clearedFilter as BoardsFilterTypes;
}

export function getBoardsFilterLocalStorage(): BoardsFilterTypes {
   const savedBoardsFilterState = localStorage.getItem("boards-filter");
   const defaultConfig = getDefaultBoardsFilter();

   if (!savedBoardsFilterState) {
      return defaultConfig;
   }

   // Merge saved state with defaults to ensure all properties are present
   const savedConfig = JSON.parse(savedBoardsFilterState);
   return { ...defaultConfig, ...savedConfig };
}

export function updateBoardsFilterLocalStorage<K extends keyof BoardsFilterTypes>(
   key: K,
   value: BoardsFilterTypes[K],
) {
   const savedConfig = localStorage.getItem("boards-filter");
   const config: Partial<BoardsFilterTypes> = savedConfig ? JSON.parse(savedConfig) : {};
   config[key] = value;
   localStorage.setItem("boards-filter", JSON.stringify(config));

   // Dispatch a custom event to notify other components
   window.dispatchEvent(
      new CustomEvent("boards-filter-update", {
         detail: {
            key: "boards-filter",
            value: JSON.stringify(config),
         },
      }),
   );
}

const BoardsMultiSelectPillFilter = ({
   projectTagOptions,
   boardsFilter,
   onFilterUpdate,
}: {
   projectTagOptions: TagOption[] | null;
   boardsFilter: BoardsFilterTypes;
   onFilterUpdate: <K extends keyof BoardsFilterTypes>(key: K, value: BoardsFilterTypes[K]) => void;
}) => {
   const tagOptionToOptionItem = (tagOption: TagOption): OptionItem => ({
      id: tagOption.id,
      label: tagOption.label,
      name: tagOption.name,
      color: tagOption.color,
      componentName: "BoardsFilterPanel",
      value: tagOption.id,
   });

   return (
      <MultiSelectPillFilter
         onChange={(value) => onFilterUpdate("projectTags", value)}
         value={boardsFilter.projectTags}
         explicitOptions={projectTagOptions?.map(tagOptionToOptionItem) ?? []}
      />
   );
};

const TokenRenderer = ({ option, removeToken }: any) => (
   <Token
      style={{
         margin: "0 4px 0 -2px",
         padding: "0",
      }}
   >
      <Token.Label
         style={{
            paddingLeft: "8px",
         }}
      >
         {option.name ?? option.label}
      </Token.Label>
      <Token.Remove
         onClick={removeToken}
         style={{
            marginRight: "1px",
         }}
      />
   </Token>
);

export function BoardsFilterPanel({
   I18n,
   isOpen,
   onClose,
   boardsFilter,
   onFilterUpdate,
   onClearAllFilters,
}: {
   I18n: I18njs;
   isOpen: boolean;
   onClose: () => void;
   boardsFilter: BoardsFilterTypes;
   onFilterUpdate: <K extends keyof BoardsFilterTypes>(key: K, value: BoardsFilterTypes[K]) => void;
   onClearAllFilters: () => void;
}) {
   const { groupId } = useGroupContext();
   const { data: jobTitles } = useGetJobTitles(groupId);
   const { data: projectCustomFieldDefinitions } = useGetProjectCustomFieldFilters(groupId);
   const { data: tagsData, isLoading: areTagsLoading } = useGetTagOptionsQuery();
   const [projectTagOptions, setProjectTagOptions] = useState<TagOption[] | null>(null);
   const { data: projectOptions, isLoading: areProjectFilterOptionsLoading } =
      useGetProjectFilterOptionsQuery(groupId);
   const [projectFilterOptions, setProjectFilterOptions] = useState<
      SelectedProjectFilterOption[] | null
   >(null);

   useEffect(() => {
      if (areTagsLoading) return;

      setProjectTagOptions(tagsData ?? null);
   }, [areTagsLoading, tagsData]);

   useEffect(() => {
      if (areProjectFilterOptionsLoading) return;
      setProjectFilterOptions(projectOptions ?? null);
   }, [areProjectFilterOptionsLoading, projectOptions]);

   return (
      <Panel
         className="b-panel b-panel-ui-procore b-sidebar"
         style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: "500px",
            height: "100%",
            zIndex: 100,
            backgroundColor: "white",
            borderRight: "1px solid #e0e0e0",
            transition: "transform 0.3s ease-in-out",
            transform: isOpen ? "translateX(0)" : "translateX(-100%)",
            visibility: isOpen ? "visible" : "hidden",
            pointerEvents: isOpen ? "auto" : "none",
         }}
      >
         <Panel.Header onClose={onClose}>
            <Panel.Title>
               {I18n.t("views.company.workforce_planning.gantt.filter.filters")}
            </Panel.Title>
            <Panel.HeaderAction title={"Reset"} onClick={onClearAllFilters}>
               <Panel.Title style={{ fontSize: "14px" }}>
                  {I18n.t("views.company.workforce_planning.gantt.filter.clear_all_filters")}
               </Panel.Title>
            </Panel.HeaderAction>
         </Panel.Header>
         <Panel.Body>
            <Box style={{ overflowY: "scroll" }}>
               <div className="boardsFilterLabel">
                  <h3 className="filterName">
                     {I18n.t("views.company.workforce_planning.project")}
                  </h3>
                  <MultiSelect
                     getLabel={(project) => project.label}
                     options={projectFilterOptions ?? []}
                     tokenRenderer={TokenRenderer}
                     value={boardsFilter.selectedProjects}
                     onChange={(value) => onFilterUpdate("selectedProjects", value)}
                     className="boardsFilterMultiselect"
                  />
               </div>
               <div className="boardsFilterLabel">
                  <h3 className="filterName">
                     {I18n.t("views.company.workforce_planning.project_tags")}
                  </h3>
                  <BoardsMultiSelectPillFilter
                     projectTagOptions={projectTagOptions}
                     boardsFilter={boardsFilter}
                     onFilterUpdate={onFilterUpdate}
                  />
               </div>
               <div className="boardsFilterLabel">
                  <h3 className="filterName">
                     {I18n.t("views.company.workforce_planning.gantt.filter.project_roles", {
                        defaultValue: "Project Roles",
                     })}
                  </h3>
                  <ProjectRolesFilter
                     value={boardsFilter.projectRoles}
                     onChange={(value) => onFilterUpdate("projectRoles", value)}
                  />
               </div>
               <div className="boardsFilterLabel">
                  <h3 className="filterName">
                     {I18n.t("views.company.workforce_planning.gantt.filter.resource_status")}
                  </h3>
                  <MultiSelect
                     getLabel={(status) => status.name}
                     options={statuses.filter((status) => {
                        // filter out the 'Pending' status
                        return status.id !== 2;
                     })}
                     tokenRenderer={TokenRenderer}
                     value={boardsFilter.resourceStatuses}
                     onChange={(value) => onFilterUpdate("resourceStatuses", value)}
                     className="boardsFilterMultiselect"
                  />
               </div>
               <div className="boardsFilterLabel">
                  <h3 className="filterName">
                     {I18n.t("views.company.workforce_planning.gantt.filter.job_title")}
                  </h3>
                  <MultiSelect
                     getLabel={(jobTitle) => jobTitle.label}
                     options={jobTitles ?? []}
                     tokenRenderer={TokenRenderer}
                     value={boardsFilter.jobTitles}
                     onChange={(value) => onFilterUpdate("jobTitles", value)}
                     className="boardsFilterMultiselect"
                  />
               </div>
               <div className="boardsFilterSwitchLabel">
                  <h3 className="filterName">
                     {I18n.t("views.company.workforce_planning.gantt.filter.hide_empty_projects")}
                  </h3>
                  <Switch
                     className="boardsFilterSwitch"
                     checked={boardsFilter.hideEmptyProjects}
                     onChange={() =>
                        onFilterUpdate("hideEmptyProjects", !boardsFilter.hideEmptyProjects)
                     }
                  />
               </div>
               <div className="boardsFilterSwitchLabel">
                  <h3 className="filterName">
                     {I18n.t("views.company.workforce_planning.gantt.filter.hide_labor")}
                  </h3>
                  <Switch
                     className="boardsFilterSwitch"
                     checked={boardsFilter.hideLabor}
                     onChange={() => onFilterUpdate("hideLabor", !boardsFilter.hideLabor)}
                  />
               </div>
               <div className="boardsFilterSwitchLabel">
                  <h3 className="filterName">
                     {I18n.t("views.company.workforce_planning.gantt.filter.hide_equipment")}
                  </h3>
                  <Switch
                     className="boardsFilterSwitch"
                     checked={boardsFilter.hideEquipment}
                     onChange={() => onFilterUpdate("hideEquipment", !boardsFilter.hideEquipment)}
                  />
               </div>
               <div className="boardsFilterSwitchLabel">
                  <h3 className="filterName">
                     {I18n.t("views.company.workforce_planning.gantt.filter.hide_assignments")}
                  </h3>
                  <Switch
                     className="boardsFilterSwitch"
                     checked={boardsFilter.hideAssignments}
                     onChange={() =>
                        onFilterUpdate("hideAssignments", !boardsFilter.hideAssignments)
                     }
                  />
               </div>
               <div className="boardsFilterSwitchLabel">
                  <h3 className="filterName">
                     {I18n.t("views.company.workforce_planning.gantt.filter.hide_requests")}
                  </h3>
                  <Switch
                     className="boardsFilterSwitch"
                     checked={boardsFilter.hideRequests}
                     onChange={() => onFilterUpdate("hideRequests", !boardsFilter.hideRequests)}
                  />
               </div>
               {projectCustomFieldDefinitions?.map((field: any) => {
                  const activeFilter =
                     boardsFilter.projectCustomFields?.find(
                        (f) => f.custom_field_id === field.id,
                     ) ?? null;
                  // Key includes whether a filter is active so React remounts
                  // the component when cleared, resetting any local state
                  // (e.g. classifier selection in number/date filters).
                  const key = `${field.id}-${activeFilter ? "active" : "empty"}`;
                  return (
                     <ProjectCustomFieldFilter
                        key={key}
                        field={field}
                        value={activeFilter}
                        onChange={(filter) => {
                           const current = boardsFilter.projectCustomFields ?? [];
                           const updated = filter
                              ? [...current.filter((f) => f.custom_field_id !== field.id), filter]
                              : current.filter((f) => f.custom_field_id !== field.id);
                           onFilterUpdate("projectCustomFields", updated);
                        }}
                     />
                  );
               })}
            </Box>
         </Panel.Body>
      </Panel>
   );
}
